import type { DataTag, InfiniteData, QueryKey } from "@tanstack/react-query";

import type {
  Mountaya,
  MountayaError,
  MountayaTransport,
  PagedResponse,
  Surface,
} from "@mountaya/client";
import { data, MountayaConfigurationError, rest, tiles } from "@mountaya/client";

import {
  mountayaKeys,
  type OperationAnswerOf,
  type OperationArgsOf,
  type OperationIdOf,
  type OperationSelectionOf,
} from "./mountayaKeys.js";

/**
 * The read of one operation as the options TanStack Query takes for it, keyed by the operation
 * itself.
 *
 * **An operation's entry holds the operation's answer as the client returns it**, whichever
 * builder or hook wrote it. A REST or Tile read caches the whole body, `data` and `extensions`
 * alike, and a reader that wants the record projects it with `select`. That is what lets the
 * operation-keyed read and a studio resource hook of the same operation share one entry: were
 * one of them to cache the record and the other the body, whichever mounted first would decide
 * the shape the other one reads.
 *
 * Nothing here imports React, so `@mountaya/client-react/query` publishes it to a server.
 */

/**
 * OperationQueryOptions is the read of one operation, as the options `useQuery`,
 * `prefetchQuery` and `ensureQueryData` take: the entry it caches under, typed by the answer it
 * holds, and the fetcher that reads it.
 *
 * Handed to TanStack's own hooks unchanged, it keeps the rule this package's hooks keep:
 * `retry` is off, because the client already retries with backoff and honours `Retry-After`,
 * and TanStack's three retries over it turn one failed call into a burst.
 */
export interface OperationQueryOptions<TAnswer> {
  /**
   * Whether a hook may run the read now: false while the client's credential scope is still to
   * be told who the session acts as (`transport.credentialScopePending`), which is from a
   * session token source's construction until its first token lands.
   *
   * The key names that scope, and the token that lands refines it, so a read run before would be
   * keyed once under the scope it was sent in and read again under the one it settles. Holding it
   * costs no time, since a request made then waits for that token anyway. `useQuery` reads it;
   * `prefetchQuery` and `ensureQueryData` run the fetcher whatever it says.
   */
  readonly enabled: boolean;
  /**
   * Reads the operation, stopping when the run it belongs to is aborted. TanStack hands each run
   * its own signal and aborts it when the run is superseded.
   */
  readonly queryFn: (context: { readonly signal?: AbortSignal | undefined }) => Promise<TAnswer>;
  /**
   * The entry the read caches under, tagged with the answer it holds, so
   * `queryClient.getQueryData(options.queryKey)` is typed without a cast.
   */
  readonly queryKey: DataTag<QueryKey, TAnswer, MountayaError>;
  /** TanStack's retries, off: the client has already retried what is worth retrying. */
  readonly retry: false;
}

/**
 * OperationRequestOptions is what one operation's call takes beside its arguments: a Data API
 * operation's field selection and its own timeout. A REST or Tile operation takes neither.
 */
export interface OperationRequestOptions<S extends Surface, Sel> {
  /** The fields a Data API operation answers. Omit it to receive the full result. */
  readonly select?: Sel | undefined;
  /**
   * How long each attempt of a Data API call waits for a response, in whole milliseconds, in
   * place of the client's `timeoutMs`.
   *
   * @default the client's own `timeoutMs`.
   */
  readonly timeoutMs?: (S extends "data" ? number : never) | undefined;
}

/**
 * taggedKey tags a key with the answer its entry holds.
 *
 * The one assertion of the builders, at the boundary between a key and the cache: a key is an
 * array at runtime, and the tag is a type-only brand TanStack reads to type `getQueryData` and
 * `setQueryData`. `queryOptions` from `@tanstack/react-query` applies the same brand, and it is
 * no import here because `./query` loads nothing of React's at runtime.
 */
export const taggedKey = <TAnswer>(key: QueryKey): DataTag<QueryKey, TAnswer, MountayaError> =>
  key as DataTag<QueryKey, TAnswer, MountayaError>;

/**
 * selectData projects the record out of a REST or Tile body.
 *
 * One function for every read, so TanStack, which re-runs a `select` whose identity changed,
 * runs it once per answer.
 */
export const selectData = <TBody extends { readonly data: unknown }>(body: TBody): TBody["data"] =>
  body.data;

/**
 * isScopeSettled answers whether a read keyed under the client's credential scope may run now:
 * the scope is not waiting for the first token of its session to say who it acts as. It is the
 * `enabled` of every builder whose key names the scope.
 */
export const isScopeSettled = (client: Mountaya): boolean =>
  !client.transport.credentialScopePending;

/**
 * keepPreviousDataWithinScope builds the `placeholderData` a hook keeps the previous answer
 * with, while the next one loads, for a read keyed by `queryKey`.
 *
 * TanStack's own `keepPreviousData` keeps the last answer the observer saw whatever key it was
 * read under, and the last answer a component saw before a change of person is the last
 * person's. So the answer is kept only where it was read under the same credential scope as the
 * read that replaces it, the first two segments of every key this package builds: a filter
 * typed into a list keeps the list on screen, and a sign-in, a change of person or a sign-out
 * shows nothing until the new scope's answer lands.
 *
 * @param queryKey The key of the read that is loading.
 */
export const keepPreviousDataWithinScope =
  (queryKey: QueryKey) =>
  <TData>(
    previousData: TData | undefined,
    previousQuery: { readonly queryKey: QueryKey } | undefined,
  ): TData | undefined =>
    previousQuery !== undefined &&
    previousQuery.queryKey[0] === queryKey[0] &&
    previousQuery.queryKey[1] === queryKey[1]
      ? previousData
      : undefined;

/** The generated operations behind each transport surface, as `new Mountaya` binds them. */
const operations: Readonly<Record<Surface, object>> = { data, rest, tiles };

/**
 * TAKES_NO_ARGUMENTS is how many parameters a generated operation declares when it takes no
 * arguments: the transport and the per-call options. Every other operation declares its
 * arguments between the two, or as the only parameter before a defaulted one, so its length
 * is three or one.
 */
const TAKES_NO_ARGUMENTS = 2;

/**
 * callOperation runs one generated operation over a transport.
 *
 * Async, so a refusal is a rejection, as TanStack reads it, for a caller running the fetcher by
 * hand too.
 *
 * @throws MountayaConfigurationError For a surface or an operation the client does not have,
 *   and for an operation that requires arguments called with none.
 */
const callOperation = async (
  transport: MountayaTransport,
  surface: Surface,
  operationId: PropertyKey,
  args: unknown,
  options: object,
): Promise<unknown> => {
  const operation: unknown = Object.hasOwn(operations, surface)
    ? Reflect.get(operations[surface], operationId)
    : undefined;

  if (typeof operation !== "function" || operationId === "bind") {
    throw new MountayaConfigurationError(
      `mountayaQueryOptions: ${String(surface)} has no operation named ${JSON.stringify(String(operationId))}. ` +
        "Name one of the operations the surface's generated module exports.",
    );
  }

  if (operation.length === TAKES_NO_ARGUMENTS) {
    return operation(transport, options);
  }

  if (args === undefined && operation.length > TAKES_NO_ARGUMENTS) {
    throw new MountayaConfigurationError(
      `mountayaQueryOptions: ${surface} ${String(operationId)} takes arguments, and none were passed. ` +
        "Pass them after the operation's name.",
    );
  }

  return operation(transport, args, options);
};

/**
 * mountayaQueryOptions reads one operation of a surface, keyed by the operation and its
 * arguments under the client's credential.
 *
 * The key is `mountayaKeys.operation`'s, so it is the one an invalidation names, and the call is
 * the operation the key names, so the two cannot disagree. The entry holds the operation's
 * answer as the client returns it: a REST or Tile body, or a Data API result narrowed by its
 * field selection, which is part of the key.
 *
 * @param client The client whose transport and credential the read uses.
 * @param surface The transport surface: `"rest"`, `"tiles"` or `"data"`.
 * @param operationId The operation, as the surface's generated module names it.
 * @param args The operation's arguments, or its input on the Data API. Omitted for an operation
 *   that takes none.
 * @param request A Data API operation's field selection and timeout.
 * @returns The options `useQuery`, `prefetchQuery` and `ensureQueryData` take.
 * @throws MountayaConfigurationError As the fetcher's rejection, before anything is sent, for a
 *   surface or an operation the client does not have and for an operation that requires
 *   arguments called with none.
 *
 * @example
 * ```ts
 * import { QueryClient } from "@tanstack/react-query";
 * import { Mountaya } from "@mountaya/client";
 * import { mountayaQueryOptions } from "@mountaya/client-react/query";
 *
 * declare const itineraryId: string;
 *
 * const queryClient = new QueryClient();
 * const mountaya = new Mountaya({ credential: { publishableKey: "pk_live_…" } });
 *
 * const body = await queryClient.fetchQuery(
 *   mountayaQueryOptions(mountaya, "rest", "getItinerary", { itinerary_id: itineraryId }),
 * );
 * console.log(body.data.name);
 * ```
 */
export const mountayaQueryOptions = <
  S extends Surface,
  Op extends OperationIdOf<S>,
  const Sel extends OperationSelectionOf<S, Op> | undefined = undefined,
>(
  client: Mountaya,
  surface: S,
  operationId: Op,
  args?: OperationArgsOf<S, Op>,
  request?: OperationRequestOptions<S, Sel>,
): OperationQueryOptions<OperationAnswerOf<S, Op, Sel>> => ({
  queryFn: ({ signal }) =>
    // The answer's type is the operation's own, which the table indexed by a name cannot carry:
    // `OperationAnswerOf` states it from the same generated module the table holds.
    callOperation(
      client.transport,
      surface,
      operationId,
      args,
      surface === "data"
        ? { select: request?.select, signal, timeoutMs: request?.timeoutMs }
        : { signal },
    ) as Promise<OperationAnswerOf<S, Op, Sel>>,
  enabled: isScopeSettled(client),
  queryKey: taggedKey(
    mountayaKeys.operation(
      client.transport.credentialScope,
      surface,
      operationId,
      args,
      request?.select,
    ),
  ),
  retry: false,
});

/**
 * PagedOperationIdOf is every REST operation that pages by cursor: a listing whose answer carries
 * its rows at `data` and the next page's cursor at `extensions.metadata.pagination.next_cursor`.
 *
 * Read off each operation's answer rather than kept as a list, so a listing the contract adds is
 * one {@link mountayaInfiniteQueryOptions} reads the day it generates. Only the REST surface
 * pages this way.
 */
export type PagedOperationIdOf = {
  [Op in OperationIdOf<"rest">]: OperationAnswerOf<"rest", Op> extends PagedResponse<unknown>
    ? Op
    : never;
}[OperationIdOf<"rest">];

/**
 * OperationInfiniteQueryOptions is the read of one paged listing, page after page, as the options
 * `useInfiniteQuery`, `prefetchInfiniteQuery` and `fetchInfiniteQuery` take: the entry it caches
 * under, the fetcher of one page, and where the next page starts.
 *
 * Each page is the operation's answer as the client returns it, the whole body. The walk starts
 * at the cursor the arguments name, or at the listing's first page, and ends at the page whose
 * `next_cursor` is `null`. `retry` is off, as on every read here, because the client already
 * retries.
 */
export interface OperationInfiniteQueryOptions<TPage> {
  /**
   * Whether a hook may start the walk now: false while the client's credential scope is still to
   * be told who the session acts as, as on {@link OperationQueryOptions.enabled}.
   */
  readonly enabled: boolean;
  /** The cursor of the page after `lastPage`, or `null` after the last page, which ends the walk. */
  readonly getNextPageParam: (lastPage: TPage) => string | null;
  /** The cursor the walk starts at: the one the arguments name, or `null` for the first page. */
  readonly initialPageParam: string | null;
  /**
   * Reads the page that starts at `pageParam`, stopping when the run it belongs to is aborted.
   */
  readonly queryFn: (context: {
    readonly pageParam: string | null;
    readonly signal?: AbortSignal | undefined;
  }) => Promise<TPage>;
  /**
   * The entry the walk caches under, tagged with the pages it holds. It extends the operation's
   * own key (`mountayaKeys.operation` for the same arguments), so invalidating the operation
   * drops the walk too, and it ends in a segment of its own, so the walk and a read of one page
   * are two entries rather than one holding two shapes.
   */
  readonly queryKey: DataTag<QueryKey, InfiniteData<TPage, string | null>, MountayaError>;
  /** TanStack's retries, off: the client has already retried what is worth retrying. */
  readonly retry: false;
}

/**
 * PAGES_SEGMENT ends the key of a walk, after the operation's own key for the same arguments.
 *
 * A walk's entry holds TanStack's pages, `{ pages, pageParams }`, where a read of one page holds
 * the body. Under one key the two would overwrite each other's shape, so the walk's key extends
 * the read's by this segment: still under the operation's prefix, never the same entry.
 */
const PAGES_SEGMENT = "pages";

/**
 * mountayaInfiniteQueryOptions reads a paged REST listing one page at a time, keyed by the
 * operation and its arguments under the client's credential, as TanStack's infinite query.
 *
 * `fetchNextPage` reads the page the last one's `next_cursor` names, and `hasNextPage` is false
 * once a page names none. The arguments are the operation's own: its filters, its `sort` and its
 * `limit` choose the listing and every page of it, and a `cursor` among them is where the walk
 * starts. Each page is the body the client answers, so a page's rows are at `data`.
 *
 * @param client The client whose transport and credential the walk uses.
 * @param operationId The listing, as the REST surface's generated module names it.
 * @param args The listing's arguments. Omitted for a listing whose arguments are all optional.
 * @returns The options `useInfiniteQuery`, `prefetchInfiniteQuery` and `fetchInfiniteQuery` take.
 * @throws MountayaConfigurationError As the fetcher's rejection, before anything is sent, for an
 *   operation the client does not have and for one that requires arguments called with none.
 *
 * @example
 * ```ts
 * import { QueryClient } from "@tanstack/react-query";
 * import { Mountaya } from "@mountaya/client";
 * import { mountayaInfiniteQueryOptions } from "@mountaya/client-react/query";
 *
 * declare const organizationId: string;
 *
 * const queryClient = new QueryClient();
 * const mountaya = new Mountaya({ credential: { publishableKey: "pk_live_…" } });
 *
 * const members = await queryClient.fetchInfiniteQuery(
 *   mountayaInfiniteQueryOptions(mountaya, "getOrganizationMembers", {
 *     organization_id: organizationId,
 *     query: { limit: 100 },
 *   }),
 * );
 * console.log(members.pages.flatMap((page) => page.data).length);
 * ```
 */
export const mountayaInfiniteQueryOptions = <Op extends PagedOperationIdOf>(
  client: Mountaya,
  operationId: Op,
  args?: OperationArgsOf<"rest", Op>,
): OperationInfiniteQueryOptions<OperationAnswerOf<"rest", Op>> => {
  // The arguments as the operation reads them, an object whatever the caller passed, so a
  // walk's key has one shape and the page's cursor joins the query the caller wrote.
  const named: { readonly query?: Readonly<Record<string, unknown>> | undefined } = args ?? {};
  const start = named.query?.cursor;

  return {
    enabled: isScopeSettled(client),
    getNextPageParam: (lastPage) =>
      // A page that names no cursor is the last one, whatever its shape says otherwise: the walk
      // ends rather than asking for a page it has no address for.
      (lastPage as PagedResponse<unknown>).extensions?.metadata?.pagination?.next_cursor ?? null,
    initialPageParam: typeof start === "string" ? start : null,
    queryFn: ({ pageParam, signal }) =>
      // The answer is the operation's own, which the table indexed by a name cannot carry:
      // `OperationAnswerOf` states it from the same generated module the table holds.
      callOperation(
        client.transport,
        "rest",
        operationId,
        // The client omits an undefined query value, so the first page of a walk that names no
        // cursor sends none.
        { ...named, query: { ...named.query, cursor: pageParam ?? undefined } },
        { signal },
      ) as Promise<OperationAnswerOf<"rest", Op>>,
    queryKey: taggedKey([
      ...mountayaKeys.operation(
        client.transport.credentialScope,
        "rest",
        operationId,
        named as OperationArgsOf<"rest", Op>,
      ),
      PAGES_SEGMENT,
    ]),
    retry: false,
  };
};
